import empty from "is-empty";

import { AuthSelectors } from "src/redux/Auth/selectors";
import fetchWrapper from "src/utils/fetchWrapper";
import { Availability } from "src/redux/Availability/types";
import { AvailabilitySelectors } from "./selectors";
import { MdActions } from "src/redux/MarketData/actions";
import { getFromLocalUserStorage } from "src/utils/localStorage";
import { Tx } from "src/redux/Tx/types";
import { TxActions } from "src/redux/Tx/actions";
import { SpinnerActions } from "src/redux/Spinner/actions";
import { TxSelectors } from "src/redux/Tx/selectors";
import { ModalActions } from "src/redux/Modal/actions";
import {
    COLLAT_TYPES,
    FETCH_STATES,
    LOAN_TYPES,
    TX_STATES,
} from "src/redux/Tx/constants";
import { getTermFromDate, setDateFormat } from "src/utils";
import { groupBy } from "src/utils";
import { VIEWS } from "src/redux/TopBar/constants";
import UploadNeedsPreview from "src/components/views/Transactions/Create/Preview";

const defaultSort = (a, b) => {
    if (a.Name === b.Name) {
        return a.Lender.localeCompare(b.Lender);
    } else {
        return a.Name.localeCompare(b.Name);
    }
};

const toAvailabilityFormat = (availability, result, enable90DayMaxMaturity) => {
    const ValueDate = result.ValueDate || availability.ValueDate;
    return {
        ...availability,
        takenNeedId: result.id,
        Quantity: result.Quantity,
        Rate:
            result.Rate === 0 || result.Rate
                ? result.Rate
                : availability.AvailableRate,
        ValueDate,
        LoanType: availability.LoanType,
        Maturity: enable90DayMaxMaturity
            ? undefined
            : availability.Maturity || new Date("9999-12-31"),
        Term: getTermFromDate(ValueDate, availability.Maturity),
        Amount: availability.PreviousClose * result.Quantity,
        marketData: {
            status: FETCH_STATES.FETCHING,
        },
        Intermediary: availability.Intermediary,
        TxType: availability.TxType,
    };
};

export const takingAvailability = (
    todayAvailability,
    needs,
    match,
    enable90DayMaxMaturity
) => {
    let newAvailability = todayAvailability
        .sort((a, b) => defaultSort(a, b))
        .map((availability, index) => {
            let result;
            if (match) {
                //upload needs > take availability
                result = needs.filter(
                    (r) =>
                        availability.security &&
                        Object.values(availability.security).includes(
                            r.SecurityId
                        ) &&
                        availability.LoanType !== LOAN_TYPES.BID &&
                        availability.AvailableQuantity >= r.Quantity
                );
                if (result.length === 0) return null;
                return result.map((r) =>
                    toAvailabilityFormat(
                        availability,
                        r,
                        index,
                        enable90DayMaxMaturity
                    )
                );
            }
            // search availability
            result = needs.filter(
                (r) =>
                    availability.security &&
                    Object.values(availability.security).includes(r.SecurityId)
            );
            if (result.length === 0) return null;
            return toAvailabilityFormat(
                availability,
                result[0],
                index,
                enable90DayMaxMaturity
            );
        })
        .filter((a) => a); // filter null value
    newAvailability = [].concat(...newAvailability);
    return {
        newAvailability,
        takenNeedIds: newAvailability.map((a) => a.takenNeedId),
    };
};

export const AvailabilityActions = {
    GetTodayAvailability: () => {
        return (dispatch, getState) => {
            const url = AuthSelectors.BuildAvailabilityUrl(getState());
            return dispatch(fetchWrapper(url))
                .then((result) => {
                    if (result) {
                        //There may be none
                        return dispatch({
                            type: Availability.NEW_AVAILABILITY_LIST,
                            availability: result,
                        });
                    }
                })
                .catch((error) => {
                    // dispatch(ToasterActions.NewError("Error fetching inventory for security")) Backend gives 404 and empty response on not found, which popped this up. Just disabling for now
                });
        };
    }, // TODO: Use MD action instead

    SetSelectedAvailability: (rows) => {
        return (dispatch) => {
            return dispatch({
                type: Availability.SET_SELECTED_AVAILABILITY,
                rows,
            });
        };
    },

    PrepareTake: (enable90DayMaxMaturity) => {
        return (dispatch, getState) => {
            const state = getState();
            const selectedRows = AvailabilitySelectors.GetSelected(state);
            dispatch({
                type: Availability.PREPARE_TAKE,
                selectedRows,
                enable90DayMaxMaturity,
            });

            const listTicker = [
                ...new Set(selectedRows.map((row) => row.ISIN)),
            ];
            dispatch(MdActions.GetMarketDataBatch(listTicker)).then((result) =>
                dispatch({
                    type: Availability.ATTACH_MARKET_DATA,
                    marketData: result,
                    listTicker,
                })
            );
        };
    },

    PrepareNeeds: (needs, match, enable90DayMaxMaturity) => {
        return (dispatch, getState) => {
            const state = getState();
            let todayAvailability =
                AvailabilitySelectors.GetAvailabilityList(state);
            const { newAvailability, takenNeedIds } = takingAvailability(
                todayAvailability,
                needs,
                match,
                enable90DayMaxMaturity
            );

            dispatch({
                type: Availability.PREPARE_NEEDS,
                newAvailability,
                enable90DayMaxMaturity,
            });
            dispatch(
                AvailabilityActions.SetNeeds({
                    pastedNeeds: needs,
                    takenNeedIds,
                })
            );

            const listTicker = [...new Set(newAvailability.map((a) => a.ISIN))];
            return dispatch(MdActions.GetMarketDataBatch(listTicker)).then(
                (result) =>
                    dispatch({
                        type: Availability.ATTACH_MARKET_DATA,
                        marketData: result,
                        listTicker: listTicker,
                    })
            );
        };
    },

    UpdateTakeRow: (rows) => {
        const { includesAC } = getFromLocalUserStorage(Tx.AC_STORAGE_TOGGLE);
        const ac = getFromLocalUserStorage(Tx.AC_STORAGE_VALUE);

        return (dispatch) => {
            dispatch({
                type: Availability.UPDATE_REQUEST_ROWS,
                rows,
            });

            dispatch(TxActions.UpdateAC(includesAC, ac, VIEWS.AVAILABILITY));
        };
    },

    ApplyBenchmarkFee: (multiplier) => ({
        type: Availability.APPLY_REF_RATE,
        multiplier,
    }),

    ApplyMaturity: (maturity, enable90DayMaxMaturity) => ({
        type: Availability.APPLY_MATURITY,
        maturity,
        enable90DayMaxMaturity,
    }),

    ResetTake: () => ({
        type: Availability.RESET_TAKE,
    }),

    DeleteRequestRow: (rowId) => {
        return (dispatch) => {
            dispatch({ type: Availability.DELETE_REQUEST_ROW, rowId });
        };
    },

    SendTake: (openAdvertise) => {
        return (dispatch, getState) => {
            const state = getState();
            dispatch(SpinnerActions.Open());
            dispatch({ type: Tx.SEND_INIT });

            const enablePurpose =
                state.Settings?.featureFlags["enable-purpose"];

            if (!AvailabilitySelectors.AvailabilityTakeIsReady(state)) {
                dispatch({ type: Tx.CANCEL_SEND });
                dispatch(ModalActions.CloseAll());
                dispatch(SpinnerActions.Close());
                return Promise.resolve();
            }

            // Assemble request settings
            const requestSettings = TxSelectors.TxInitRequestSettings(state);

            let collateralType = { ...requestSettings.collateralType };
            if (empty(collateralType.type)) collateralType.type = null;
            if (
                empty(collateralType.ccy) ||
                collateralType.type === COLLAT_TYPES.NC
            )
                delete collateralType.cash;
            if (empty(collateralType.nonCash.schedule))
                delete collateralType.nonCash;

            // Assemble acceptance criteria
            let ac = getFromLocalUserStorage(Tx.AC_STORAGE_VALUE);
            const { includesAC } = getFromLocalUserStorage(
                Tx.AC_STORAGE_TOGGLE
            );
            if (includesAC) {
                const disables = TxSelectors.TxInitDisables(state);
                if (disables.includes("maxRateBps")) {
                    ac.maxRateBps = "";
                }
            }
            const url = AuthSelectors.BuildTxBorrowInitUrl(state);
            let acceptanceCriteria = { preferredType: LOAN_TYPES.LOAN };
            if (includesAC) {
                acceptanceCriteria = { ...acceptanceCriteria, ...ac };
            }

            // Internal fund code
            const { value: fundName } = getFromLocalUserStorage(
                Tx.FUND_CODE_STORAGE_VALUE
            );

            const Availabilities = AvailabilitySelectors.GetRequestRows(state);
            // assemble request payload per lender
            const takeRowData = groupBy(Availabilities, "Lender");

            let promises = [];
            for (const rows of Object.values(takeRowData)) {
                let lender;
                const requestRowData = rows.map((row, idx) => {
                    if (idx === 0) {
                        lender = row.lender;
                    }
                    let rowOutput = {
                        quantity: row.Quantity,
                        security: row.marketData.data,
                        type: row.LoanType,
                        valueDate: setDateFormat(row.ValueDate, "-"),
                        callable: empty(requestSettings.callable)
                            ? null
                            : requestSettings.callable,
                        intermediary: row.Intermediary,
                        txType: row.TxType,
                        collateralType,
                        maturityDate:
                            setDateFormat(row.Maturity, "-") || "9999-12-31",
                    };

                    if (row.Rate !== null) {
                        if (row.FR === "R") {
                            rowOutput.rateRebate = {
                                rateBp: Math.round(row.Rate * 100),
                            };
                        } else {
                            rowOutput.fee = {
                                rateBp: Math.round(row.Rate * 100),
                            };
                        }
                    }

                    if (
                        row.Dividend !== undefined &&
                        row.Dividend !== null &&
                        row.Dividend !== ""
                    ) {
                        rowOutput.dividend = row.Dividend;
                    }

                    if (row.Purpose && enablePurpose) {
                        rowOutput.purpose = row.Purpose;
                    }

                    rowOutput.lastUser =
                        AuthSelectors.User(state).attributes.email;
                    return rowOutput;
                });

                const request = {
                    txSet: requestRowData,
                    user: {
                        email: AuthSelectors.User(state).attributes.email,
                    },
                    lenders: [lender],
                    acceptanceCriteria,
                    fundName,
                };

                promises.push(
                    dispatch(
                        fetchWrapper(url, {
                            method: "POST",
                            body: JSON.stringify(request),
                            headers: {
                                Accept: "application/json",
                                "Content-Type": "application/json",
                            },
                        })
                    ).then((result) => {
                        dispatch({
                            type: Tx.INIT_SENT,
                            correlationId: result.correlationId,
                        });
                        dispatch({
                            type: Tx.INSERT_BORROW_TX_LIST,
                            txSet: result.txSet.map((tx) => ({
                                ...tx,
                                state: TX_STATES.PRE_LOAN_REQUESTED,
                            })),
                            state: TX_STATES.PRE_LOAN_REQUESTED,
                        });
                    })
                );
            }

            return Promise.all(promises)
                .then(() => {
                    // TODO: need to extract an action for resetting only settings except tx create rows
                    // dispatch({ type: Tx.RESET_INIT });
                    dispatch(SpinnerActions.Close());
                    return dispatch(ModalActions.CloseAll());
                })
                .then(async () => {
                    if (openAdvertise) {
                        // Open Advertise Needs screen
                        // TODO: Add needs which are already taken but the qty is bigger than available qty
                        const needs = AvailabilitySelectors.GetNeedsToAdvertise(
                            getState()
                        );
                        if (needs.length > 0) {
                            await dispatch(TxActions.PrepareInit(needs));
                            await dispatch(
                                ModalActions.Open(UploadNeedsPreview, {
                                    title: "Preview",
                                    large: true,
                                    padding0: true,
                                    ignoreClickOutside: true,
                                    onClose: () =>
                                        dispatch(TxActions.ResetInit()),
                                })
                            );
                        }
                    }
                    return dispatch({ type: Availability.RESET_TAKE });
                })
                .catch((error) => {
                    dispatch(SpinnerActions.Close());
                    dispatch({ type: Tx.INIT_ERROR, error });
                });
        };
    },

    SetNeeds: (obj) => {
        return (dispatch) => {
            return dispatch({
                type: Availability.AVAILABILITY_SET_NEEDS,
                ...obj,
            });
        };
    },
};
